import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./EditMeetingInfoModal.module.css";
import CalenderDateIcon from "../CalenderDateIcon";
import InPersonMeetingIcon from "../InPersonMeetingIcon";
import VirtualMeetingIcon from "../VirtualMeetingIcon";
import Modal from "../Modal";
import ModalCard from "../ModalCard";
import ModalActionBar from "shared/ModalActionBar";
import { ReactComponent as EditButtonImg } from "../../assets/Edit_Category_Edit.svg";
import { ReactComponent as DoneButtonImg } from "../../assets/Edit_Category_Done.svg";
import { ReactComponent as AddButtonImg } from "../../assets/Add_to_Calendar_Button.svg";
import { ReactComponent as CancelButtonImg } from "../../assets/Edit_Category_Cancel.svg";
import { ReactComponent as SaveButtonImg } from "../../assets/Edit_Category_Save.svg";
import ZoomIcon from "../../assets/Zoom_Logo.png";

/**
 * Interactive component. A modal that shows all the necessary information for the meeting thtat the user is previewing, including Zoom Meeting information.
 * The user clicks on the done button to close the modal.
 * Clicking the edit button will let the user edit the link information.
 */
export function EditMeetingInfoModal({
  years,
  months,
  days,
  location,
  building,
  city,
  state,
  time,
  zoomLink,
  meetingId,
  meetingPasscode,
  addToCalender,
  isInPerson,
  isVirtual,
  isOpen,
  onClose,
  onSave,
  readonly,
}) {
  const [isEdit, setIsEdit] = useState(false);
  const [currentLink, setCurrentLink] = useState(zoomLink);

  return (
    <Modal
      isOpen={isOpen}
      children={
        <>
          <div className={classNames(styles["button-bar"])}>
            <ModalActionBar
              children={
                <>
                  {!readonly && (
                    <button
                      type="button"
                      onClick={() => {
                        if (isEdit) {
                          setCurrentLink(zoomLink);
                        }
                        setIsEdit(!isEdit);
                      }}
                    >
                      {isEdit ? <CancelButtonImg /> : <EditButtonImg />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={
                      isEdit
                        ? () => {
                            onSave(currentLink);
                            setIsEdit(false);
                          }
                        : onClose
                    }
                  >
                    {isEdit ? <SaveButtonImg /> : <DoneButtonImg />}
                  </button>
                </>
              }
            />
          </div>
          <div className={classNames(styles.card)}>
            <ModalCard
              children={
                <div className={classNames(styles["card-content"])}>
                  <div className={classNames(styles["card-setting"])}>
                    <CalenderDateIcon years={years} months={months} days={days} />
                    <div className={classNames(styles.setting)}>
                      <span className={classNames(styles.bold)}>
                        {city}
                        {state && `, ${state}`}
                      </span>
                      <span className={classNames(styles.building)}>
                        {location}
                      </span>
                      <span className={classNames(styles.time)}>{time}</span>
                      <div
                        className={classNames(
                          styles["icon-bar"],
                          styles.regular
                        )}
                      >
                        {isInPerson && <InPersonMeetingIcon />}
                        {isVirtual && <VirtualMeetingIcon />}
                      </div>
                    </div>
                  </div>
                  <div className={classNames(styles["card-zoom"])}>
                    <div className={classNames(styles["card-zoom-link"])}>
                      <img
                        src={ZoomIcon}
                        alt="Zoom icon"
                        className={classNames(styles["zoom-icon"])}
                      />
                      <div className={classNames(styles["zoom-link"])}>
                        <label htmlFor="zoom-link">Zoom Link:</label>
                        <div className={classNames(styles["link-container"])}>
                          {!readonly && (
                            <textarea
                              name="zoom-link"
                              required
                              pattern="https://.*"
                              className={classNames(styles.input, {
                                [styles.hidden]: !isEdit,
                              })}
                              onChange={(key) =>
                                setCurrentLink(key.target.value)
                              }
                              value={currentLink}
                            />
                          )}
                          {readonly && (
                            <a
                              href={zoomLink}
                              target="_blank"
                              rel="noreferrer"
                              className={classNames(styles.input, {
                                [styles.hidden]: isEdit,
                              })}
                            >
                              {zoomLink}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={classNames(styles["meeting-info"])}>
                      <p>
                        <span className={classNames(styles.light)}>
                          Meeting ID:
                        </span>{" "}
                        {meetingId}
                      </p>
                      <p>
                        <span className={classNames(styles.light)}>
                          Meeting Passcode:
                        </span>{" "}
                        {meetingPasscode}
                      </p>
                    </div>
                  </div>
                  {/* <div className={classNames(styles.line)} />
                  <div className={classNames(styles["add-container"])}>
                    <button type="button" onClick={addToCalender}>
                      <AddButtonImg />
                    </button>
                    <p>Add to Mobile Calendar</p>
                  </div> */}
                </div>
              }
            />
          </div>
        </>
      }
    />
  );
}

EditMeetingInfoModal.propTypes = {
  /**
   * String that displays the month of the meeting.
   */
  month: PropTypes.string,
  /**
   * Array of strings to display the dates of the event.
   */
  days: PropTypes.arrayOf(PropTypes.string),
  /**
   * City and state where the meeting will be located.
   */
  location: PropTypes.string,
  /**
   * Building where the meeting is being held.
   */
  building: PropTypes.string,
  /**
   * Time of the meeting.
   */
  time: PropTypes.string,
  /**
   * Link to the Zoom meeting.
   */
  zoomLink: PropTypes.string,
  /**
   * ID of the Zoom meeting.
   */
  meetingId: PropTypes.string,
  /**
   * Passcode of the Zoom meeting.
   */
  meetingPasscode: PropTypes.string,
  /**
   * Function that adds the meeting to the user's mobile calender.
   */
  addToCalender: PropTypes.func,
  /**
   * Boolean that determines if the in-person icon is displayed.
   */
  isInPerson: PropTypes.bool,
  /**
   * Boolean that determines if the virtual icon is displayed.
   */
  isVirtual: PropTypes.bool,
  /**
   * Boolean that determines if the modal is displayed.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * Function that fires when the save button for the zoom link is clicked.
   */
  onSave: PropTypes.func,
};
